import {
  CartItemGroup,
  CartItemGroups,
  CartItemOption,
  CartItemOptions,
  CartLevels,
} from '@open-tender/types'
import { Heading } from 'components'
import { isIngredientGroup } from 'utils/assortment'
import MenuItemGroups from '../MenuItemGroups'
import {
  AssortmentBack,
  AssortmentCustomizeBody,
  AssortmentCustomizeHeader,
  AssortmentIngredientActions,
  AssortmentIngredientRow,
  AssortmentIngredients,
  AssortmentIngredientTitle,
  AssortmentSegment,
  AssortmentSegmented,
  AssortmentTextButton,
} from './MenuItemAssortment.styled'

type Handlers = {
  toggleOption: (levels: CartLevels) => void
  incrementOption: (levels: CartLevels) => void
  decrementOption: (levels: CartLevels) => void
  setOptionQuantity: (
    levels: CartLevels,
    quantity: number,
    nested?: CartItemOptions
  ) => void
}

const AssortmentIngredientList = ({
  group,
  levels,
  toggleOption,
  incrementOption,
  decrementOption,
}: {
  group: CartItemGroup
  levels: CartLevels
} & Pick<Handlers, 'toggleOption' | 'incrementOption' | 'decrementOption'>) => {
  const atMax = group.max !== 0 && group.quantity >= group.max
  const belowMin = group.quantity < group.min
  const count = group.max ? ` (${group.quantity}/${group.max})` : ''
  const selected = group.options.filter((option) => option.quantity > 0)
  const available = group.options.filter((option) => option.quantity === 0)

  return (
    <AssortmentIngredients>
      <AssortmentIngredientTitle
        size="main"
        as="h3"
        className={belowMin ? 'required' : undefined}
        tabIndex={belowMin ? -1 : undefined}
        invalid={belowMin}
      >
        {group.name}
        {count}
      </AssortmentIngredientTitle>
      {selected.map((ingredient) => {
        const choice = ingredient.groups[0]
        return (
          <AssortmentIngredientRow key={ingredient.id}>
            <Heading size="small">{ingredient.name}</Heading>
            <AssortmentIngredientActions>
              <AssortmentSegmented
                role="radiogroup"
                aria-label={ingredient.name}
              >
                {choice.options.map((value) => (
                  <AssortmentSegment
                    key={value.id}
                    type="button"
                    role="radio"
                    aria-checked={value.quantity > 0}
                    selected={value.quantity > 0}
                    onClick={() =>
                      toggleOption([
                        ...levels,
                        [group.id, ingredient.id],
                        [choice.id, value.id],
                      ])
                    }
                  >
                    {value.name}
                  </AssortmentSegment>
                ))}
              </AssortmentSegmented>
              <AssortmentTextButton
                type="button"
                aria-label={`Remove ${ingredient.name}`}
                onClick={() =>
                  decrementOption([...levels, [group.id, ingredient.id]])
                }
              >
                Remove
              </AssortmentTextButton>
            </AssortmentIngredientActions>
          </AssortmentIngredientRow>
        )
      })}
      {available.map((ingredient) => (
        <AssortmentIngredientRow key={ingredient.id}>
          <Heading size="small" style={{ opacity: 0.6 }}>
            {ingredient.name}
          </Heading>
          <AssortmentTextButton
            type="button"
            aria-label={`Add ${ingredient.name}`}
            disabled={atMax || ingredient.isSoldOut}
            title={atMax ? 'Remove another item to add this one' : undefined}
            onClick={() =>
              incrementOption([...levels, [group.id, ingredient.id]])
            }
          >
            {ingredient.isSoldOut ? 'Out of stock' : 'Add'}
          </AssortmentTextButton>
        </AssortmentIngredientRow>
      ))}
    </AssortmentIngredients>
  )
}

const AssortmentCustomize = ({
  slot,
  option,
  onBack,
  ...handlers
}: {
  slot: CartItemGroup
  option: CartItemOption
  onBack: () => void
} & Handlers) => {
  const levels: CartLevels = [[slot.id, option.id]]
  const groups: CartItemGroups = option.groups

  return (
    <>
      <AssortmentCustomizeHeader>
        <AssortmentBack type="button" aria-label="Back" onClick={onBack}>
          &larr;
        </AssortmentBack>
        <div>
          <Heading as="h1" size="big">
            {option.name}
          </Heading>
          <Heading as="div" size="small">
            {slot.name}
          </Heading>
        </div>
      </AssortmentCustomizeHeader>
      <AssortmentCustomizeBody>
        {groups.map((group) =>
          isIngredientGroup(group) ? (
            <AssortmentIngredientList
              key={group.id}
              group={group}
              levels={levels}
              toggleOption={handlers.toggleOption}
              incrementOption={handlers.incrementOption}
              decrementOption={handlers.decrementOption}
            />
          ) : (
            <MenuItemGroups
              key={group.id}
              groups={[group]}
              levels={levels}
              isChild
              {...handlers}
            />
          )
        )}
      </AssortmentCustomizeBody>
    </>
  )
}

export default AssortmentCustomize
